import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import RenewalReviewCard from '../components/RenewalReviewCard';
import FlatLimitDialog, { FlatLimitInfo, FlatUnit } from '../components/FlatLimitDialog';

type ApprovalTab = 'email' | 'manual';

const ApprovalsPage: React.FC = () => {
  const { token, refreshSubscriptionStatus } = useAuth();
  const { refreshPendingRenewalsCount } = useOutletContext<{ refreshPendingRenewalsCount: () => void }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [counts, setCounts] = useState({ email: 0, manual: 0 });
  const tabParam = searchParams.get('tab');
  // With no explicit tab, open the one that actually has something waiting
  const tab: ApprovalTab = tabParam === 'manual' || tabParam === 'email'
    ? tabParam
    : counts.email === 0 && counts.manual > 0 ? 'manual' : 'email';
  const [emailRenewals, setEmailRenewals] = useState<any[]>([]);
  const [manualRenewals, setManualRenewals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: 'Bearer ' + token };

  const [flatBlock, setFlatBlock] = useState<FlatLimitInfo | null>(null);
  const flatResolver = useRef<((r: { unit?: FlatUnit } | null) => void) | null>(null);
  const askFlatResolution = (info: FlatLimitInfo) =>
    new Promise<{ unit?: FlatUnit } | null>(resolve => { flatResolver.current = resolve; setFlatBlock(info); });
  const resolveFlatBlock = (result: { unit?: FlatUnit } | null) => {
    setFlatBlock(null);
    flatResolver.current?.(result);
    flatResolver.current = null;
  };

  const refresh = useCallback(async () => {
    const auth = { headers: { Authorization: 'Bearer ' + token } };
    try {
      const [countsRes, emailRes, manualRes, parkedRes] = await Promise.all([
        axios.get(config.apiUrl + '/api/approvals/counts', auth),
        axios.get(config.apiUrl + '/api/pending-renewals?source=email', auth),
        axios.get(config.apiUrl + '/api/pending-renewals?source=manual', auth),
        axios.get(config.apiUrl + '/api/pending-certificates', auth),
      ]);
      setCounts({ email: countsRes.data.email || 0, manual: countsRes.data.manual || 0 });
      setEmailRenewals(emailRes.data.pending_renewals || []);
      setManualRenewals([
        ...(manualRes.data.pending_renewals || []),
        ...(parkedRes.data.pending_certificates || []),
      ]);
    } catch (e) {
      console.error('Failed to load approvals:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const afterChange = async () => {
    await refresh();
    refreshPendingRenewalsCount();
    refreshSubscriptionStatus();
  };

  const handleApprove = async (renewalId: string, overrideCertificateId?: string, edits?: Record<string, unknown>) => {
    let currentEdits = edits;
    for (;;) {
      try {
        await axios.post(
          config.apiUrl + '/api/pending-renewals/' + renewalId + '/approve',
          { override_certificate_id: overrideCertificateId, edits: currentEdits },
          { headers }
        );
        break;
      } catch (err: any) {
        const data = err.response?.data;
        if (err.response?.status === 403 && data?.code === 'FLAT_LIMIT_REACHED') {
          // Buy another flat and retry as is, or re-point the certificate to a flat that already exists
          const resolution = await askFlatResolution(data);
          if (!resolution) throw new Error('Not added: it would go over the flats on your plan.');
          if (resolution.unit) {
            currentEdits = {
              ...currentEdits,
              normalized_address: resolution.unit.normalized_address,
              flat_number: resolution.unit.flat === 'Whole Property' ? [] : [resolution.unit.flat],
            };
          }
          continue;
        }
        throw new Error(data?.error || err.message);
      }
    }
    await afterChange();
  };

  const handleReject = async (renewalId: string) => {
    await axios.post(config.apiUrl + '/api/pending-renewals/' + renewalId + '/reject', {}, { headers });
    await afterChange();
  };

  const renderRenewals = (renewals: any[]) => (
    <Box sx={{ mb: 3 }}>
      {renewals.map(renewal => (
        <RenewalReviewCard
          key={renewal.id}
          renewal={renewal}
          onApprove={handleApprove}
          onReject={handleReject}
          token={token || ''}
        />
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <FlatLimitDialog info={flatBlock} token={token || ''} onResolve={resolveFlatBlock} />
      <Tabs value={tab} onChange={(_, value) => setSearchParams({ tab: value })} sx={{ mb: 3 }}>
        <Tab value="email" label={'From email' + (counts.email > 0 ? ' (' + counts.email + ')' : '')} />
        <Tab value="manual" label={'Manual uploads' + (counts.manual > 0 ? ' (' + counts.manual + ')' : '')} />
      </Tabs>

      {tab === 'email' ? (
        emailRenewals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No certificates from email are waiting for approval. When a registered provider emails you a certificate, it appears here.
          </Typography>
        ) : (
          renderRenewals(emailRenewals)
        )
      ) : manualRenewals.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing is waiting for your approval. Certificates you upload but don't add are saved here.
        </Typography>
      ) : (
        renderRenewals(manualRenewals)
      )}
    </Box>
  );
};

export default ApprovalsPage;
